import { memo, useEffect, useRef, useMemo } from 'react';
import classnames from 'classnames';
import { useMeasure } from 'react-use';
import { useSpring, animated } from 'react-spring';
import { isMobileOnly as isMobile } from 'react-device-detect';

import { useMedia } from 'utils/hooks';

import styles from './index.css';

export interface Intro {
    content: string;
    type: 'EN' | 'ZH';
}

interface Nav {
    text: string;
    value: string;
}

export interface MenuProp {
    className?: string;
    active?: boolean;
    intro?: Intro[];
    navLinks?: Nav[];
    fixedLink?: {
        text: string;
        url: string;
    };
    onClickNav?: (value: string) => void;
}

const Menu: React.FC<MenuProp> = ({
    className,
    active = true,
    intro = [],
    navLinks = [],
    fixedLink,
    onClickNav = () => {},
}) => {
    const introRef = useRef<HTMLDivElement>(null);

    const [navWrapperRef, { height: navHeight }] = useMeasure<HTMLDivElement>();
    const [fixedLinkRef, { height: fixedLinkHeight }] = useMeasure<HTMLAnchorElement>();
    const { media, mediaScale } = useMedia();

    const { x } = useSpring({
        // x: active || media === 'phone' ? 0 : 400 * mediaScale,
        x: active || (media === 'phone' && isMobile) ? 0 : 400 * mediaScale,
    });

    const bottomHeight = useMemo(() => {
        if (isMobile) {
            return navLinks.length * 28 + 14 + (fixedLink ? 14 + 33 : 0);
        }

        return navHeight + 28 + (fixedLink ? fixedLinkHeight + 33 * mediaScale : 0);
    }, [navHeight, fixedLink, fixedLinkHeight, mediaScale]);

    useEffect(() => {
        if (!active) {
            introRef.current?.scrollTo(0, 0);
        }
    }, [active]);

    return (
        <animated.div
            className={classnames(
                styles.menu,
                active && styles.active,
                isMobile && styles.mobile,
                className,
            )}
            style={{ translateX: x }}
        >
            <div className={styles.contentWrapper}>
                {intro.length > 0 && (
                    <div
                        ref={introRef}
                        className={styles.intro}
                        style={{
                            maxHeight: `calc(100% - ${bottomHeight}px)`,
                        }}
                    >
                        {intro.map(({ content, type }) => (
                            <div className={classnames(styles.content, styles[type])} key={content}>
                                {content}
                            </div>
                        ))}
                    </div>
                )}
                {navLinks.length > 0 && (
                    <div ref={navWrapperRef} className={styles.navWrapper}>
                        {navLinks.map(({ text, value }) => (
                            <div
                                key={text}
                                className={styles.navItem}
                                onClick={(event) => {
                                    onClickNav(value);
                                    event.stopPropagation();
                                }}
                                onKeyPress={() => {}}
                                role="menuitem"
                                tabIndex={0}
                            >
                                {text}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {fixedLink && (
                <a
                    ref={fixedLinkRef}
                    className={styles.fixedLink}
                    href={fixedLink.url}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    {fixedLink.text}
                </a>
            )}
        </animated.div>
    );
};

export default memo(Menu);
